<template>
  <div class="container">
    <v-card class="mx-auto">
      <v-card-title>Patient Profile</v-card-title>
      <v-card-text>
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-row>
            <v-col cols="6" sm="6" md="6">
              <v-text-field
                  placeholder="Transmittal ID"
                  outlined
                  v-model="formData.patient.transmittal_id"
                  :counter="21"
                  :rules="rules.transmittal_id"
                  label="E-claims Transmittal ID Number"
                  required
              />
              <v-text-field
                  placeholder="Patient PIN"
                  outlined
                  v-model="formData.patient.patient_pin"
                  :counter="12"
                  :rules="rules.pin"
                  :readonly="formData.patient.sureclaims"
                  label="Patient PhilHealth Identification Number"
                  required
              />
              <v-text-field
                  placeholder="Claim ID"
                  outlined
                  v-model="formData.patient.claim_id"
                  :counter="21"
                  :rules="rules.claim_id"
                  :readonly="formData.patient.sureclaims"
                  label="Claim ID Number"
                  required
              />
            </v-col>

            <v-col cols="6" sm="6" md="6">
              <v-text-field
                  placeholder="Last Name"
                  outlined
                  v-model="formData.patient.last_name"
                  :rules="[v => !!v || 'Last name is required']"
                  :readonly="formData.patient.sureclaims"
                  label="Last Name"
                  required
              />
              <v-text-field
                  placeholder="First Name"
                  outlined
                  v-model="formData.patient.first_name"
                  :rules="[v => !!v || 'First name is required']"
                  label="First Name"
                  :readonly="formData.patient.sureclaims"
                  required
              />
              <v-text-field
                  placeholder="Middle Name"
                  outlined
                  v-model="formData.patient.middle_name"
                  :readonly="formData.patient.sureclaims"
                  label="Middle Name"
              />
              <v-text-field
                  placeholder="Suffix"
                  outlined
                  v-model="formData.patient.suffix"
                  :readonly="formData.patient.sureclaims"
                  label="Suffix"
              />
            </v-col>
          </v-row>

          <v-row>
            <!-- Admission Date-->
            <v-col cols="6" sm="6" md="6">
              <v-textarea
                  v-model="formData.patient.admitting_diagnosis"
                  outlined
                  name="input-7-4"
                  label="Admitting Diagnosis"
                  :disabled="formData.patient.sureclaims"
                  rows="4"
              ></v-textarea>
              <v-menu
                  v-model="admissionDate"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  :disabled="formData.patient.sureclaims"
                  max-width="290px"
                  min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                      v-model="formData.patient.admission_date"
                      :disabled="formData.patient.sureclaims"
                      label="Admission Date"
                      placeholder="YYYY-MM-DD"
                      persistent-hint
                      outlined
                      readonly
                      clearable
                      v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="formData.patient.admission_date" no-title
                               @input="admissionDate = false"
                ></v-date-picker>
              </v-menu>
              <!-- Discharge Date-->
              <v-menu
                  v-model="dischargeDate"
                  :disabled="formData.patient.sureclaims"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                      v-model="formData.patient.discharge_date"
                      label="Discharge Date"
                      :disabled="formData.patient.sureclaims"
                      placeholder="YYYY-MM-DD"
                      persistent-hint
                      outlined
                      readonly
                      clearable
                      v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="formData.patient.discharge_date" no-title
                               @input="dischargeDate = false"></v-date-picker>
              </v-menu>
            </v-col>
            <!--Admission Time-->
            <v-col cols="6" sm="6" md="6">
              <v-textarea
                  v-model="formData.patient.discharge_diagnosis"
                  outlined
                  :disabled="formData.patient.sureclaims"
                  name="input-7-4"
                  label="Discharge Diagnosis"
                  rows="4"
              ></v-textarea>
              <v-menu
                  ref="admissionTime"
                  v-model="admTime"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  :disabled="formData.patient.sureclaims"
                  max-width="290px"
                  min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                      v-model="formData.patient.admission_time"
                      :disabled="formData.patient.sureclaims"
                      label="Admission Time"
                      placeholder="HH:MM A"
                      persistent-hint
                      outlined
                      readonly
                      clearable
                      v-on="on"
                  ></v-text-field>
                </template>
                <v-time-picker
                    v-if="admTime"
                    v-model="admissionTime"
                    format="ampm"
                    :disabled="formData.patient.sureclaims"
                    no-title
                    @change="timeSave(admissionTime, 0)"
                ></v-time-picker>
              </v-menu>
              <!--Discharge Time-->
              <v-menu
                  ref="dischargeTime"
                  v-model="disTime"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  :disabled="formData.patient.sureclaims"
                  max-width="290px"
                  min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                      v-model="formData.patient.discharge_time"
                      label="Discharge Time"
                      placeholder="HH:MM A"
                      persistent-hint
                      outlined
                      :disabled="formData.patient.sureclaims"
                      clearable
                      v-on="on"
                  ></v-text-field>
                </template>
                <v-time-picker
                    v-if="disTime"
                    v-model="dischargeTime"
                    format="ampm"
                    no-title
                    :disabled="formData.patient.sureclaims"
                    @change="timeSave(dischargeTime, 1)"
                ></v-time-picker>
              </v-menu>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="6" sm="6" md="6">
              <v-text-field
                  placeholder="First Case Rate Code"
                  outlined
                  :disabled="formData.patient.sureclaims"
                  v-model="formData.patient.first_case_rate_code"
                  label="First Case Rate Code"
                  :readonly="formData.patient.sureclaims"
              />
            </v-col>
            <v-col cols="6" sm="6" md="6">
              <v-text-field
                  placeholder="Second Case Rate Code"
                  outlined
                  v-model="formData.patient.second_case_rate_code"
                  label="Second Case Rate Code"
                  :readonly="formData.patient.sureclaims"

              />
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <v-menu
                  ref="dob"
                  v-model="menu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                      v-model="formData.patient.dob"
                      label="Date of Birth"
                      :disabled="formData.patient.sureclaims"
                      :rules="[v => !!v || 'Date of Birth is required']"
                      required
                      readonly
                      outlined
                      v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                    ref="dobpicker"
                    :disabled="formData.patient.sureclaims"
                    v-model="formData.patient.dob"
                    :max="new Date().toISOString().substr(0, 10)"
                    min="1900-01-01"
                    @change="save"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col>
              <v-select
                  placeholder="Gender"
                  outlined
                  v-model="formData.patient.gender"
                  :items="gender"
                  item-value="type"
                  item-text="value"
                  :disabled="formData.patient.sureclaims"
                  :rules="[v => !!v || 'Gender is required']"
                  label="Gender"
                  required
                  clearable
              />
            </v-col>
            <v-col>
              <v-select
                  placeholder="Patient Type"
                  outlined
                  v-model="formData.patient.patient_type"
                  :items="patientType"
                  item-value="type"
                  item-text="value"
                  :disabled="formData.patient.sureclaims"
                  :rules="[v => !!v || 'Patient type is required']"
                  label="Patient Type"
                  required
                  clearable
              ></v-select>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-card-actions right>
        <v-spacer></v-spacer>
        <v-btn
            :disabled="!valid"
            @click="validate"
            color="success"
            class="mr-4">
          NEXT
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import rules from '@/components/core/rules';
import moment from 'moment';

export default {
  name: "PatientProfile",

  props: {
    currentTab: Number,
    formData: {
      required: false,
      type: Object
    }
  },

  data() {
    return {
      menu: false,
      valid: false,
      rules: rules,
      gender: [],
      patientType: [],
      admissionDate: false,
      dischargeDate: false,
      loading: false,
      admissionTime: null,
      dischargeTime: null,
      admTime: false,
      disTime: false
    }
  },

  watch: {
    menu(val) {
      val && setTimeout(() => (this.$refs.dobpicker.activePicker = 'YEAR'))
    },

    date(val) {
      this.dateFormatted = this.formatDate(this.formData.patient.admission_date)
    },
  },

  computed: {
    // computedDateFormatted: {
    //     get() {
    //         return this.parseDate(this.formData.patient.admission_date);
    //     },
    //     set(value) {
    //         this.formData.patient.admission_date = value;
    //     },
    // },
    // computedTimeFormatted: {
    //   get() {
    //     return this.parseDate(this.formData.patient.admission_date);
    //   },
    //   set(value) {
    //     this.formData.patient.admission_date = value;
    //   },
    // }
  },
  methods: {
    timeSave(time, cat) {
      if (cat) {
        this.$refs.dischargeTime.save(time)
        this.formData.patient.discharge_time = moment(time, "HH:mm").format('hh:mm a')
      } else {
        this.$refs.admissionTime.save(time)
        this.formData.patient.admission_time = moment(time, "HH:mm").format('hh:mm a')
      }
    },
    save(date) {
      console.log(date)
      this.$refs.dob.save(date)
    },
    validate() {
      if (this.$refs.form.validate()) {
        this.$bus.emit('nextTab', this.currentTab + 1);
      }
    },
    reset() {
      this.$refs.form.reset()
    },
    resetValidation() {
      this.$refs.form.resetValidation()
    },

    formatDate(date) {
      if (!date) return null;

      const [year, month, day] = date.split('-');
      return `${month}/${day}/${year}`
    },

    parseDate(date) {
      if (!date) return null;

      const [year, month, day] = date.split('-');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    },

    getGender() {
      this.axios.get('config-gender', {
        headers: {
          'Authorization': 'Bearer ' + localStorage.getItem('AUTH_TOKEN'),
        }
      }).then((response) => {
        // handle success
        this.gender = response.data;
        this.loading = true;
      }).catch((error) => {
        // handle error
        console.log(error);
      }).finally(() => {
        // always executed
      });
    },
    getPatientType() {
      this.axios.get('config-patient-type', {
        headers: {
          'Authorization': 'Bearer ' + localStorage.getItem('AUTH_TOKEN'),
        }
      }).then((response) => {
        // handle success
        this.patientType = response.data;
      }).catch((error) => {
        // handle error
        console.log(error);
      }).finally(() => {
        // always executed
      });
    },
    getAdmissionAndDischargeTime() {
      this.formData.patient.admission_time = this.formData.patient.admission_time
          ? moment(this.formData.patient.admission_time, "HH:mm").format('hh:mm a')
          : '';
      this.formData.patient.discharge_time = this.formData.patient.admission_time
          ? moment(this.formData.patient.discharge_time, "HH:mm").format('hh:mm a')
          : '';
    }
  },

  mounted() {
    this.getGender();
    this.getPatientType();
    this.getAdmissionAndDischargeTime();
  }
}
</script>

<style scoped>

</style>
